import React, { useState, useEffect } from "react";
import { Box, Button, IconButton, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import DataGridCustomToolbar from "../../components/DataGridCustomToolbar";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import axios from "axios";
import SendIcon from "@mui/icons-material/Send";
import { useGetUserActivityQuery } from "../../state/api";

const UserActivity = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [rowData, setRowData] = useState([]);
  const { data, isLoading } = useGetUserActivityQuery();
  const [dates, setDates] = useState({ fromDate: "", toDate: "" });

  useEffect(() => {
    if (data && data.success) {
      setRowData(data.data || []);
    }
  }, [data]);

  const fetchData = async () => {
    try {
      const response = await axios.get("/user/userFullname");
      setUsers(response.data.profile);
      setIsDataFetched(true);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleOpen = () => {
    if (!isDataFetched) {
      fetchData();
    }
  };

  const dispatch = useDispatch();

  const [searchInput, setSearchInput] = useState("");

  let [filterData, setFilterData] = useState([]);
  const [filterSelected, setFilterSelected] = useState(false);

  const handleChange = (event, type) => {
    const value = event.target.value;
    if (value === "all") {
      setDates({ fromDate: "", toDate: "" });
      setFilterData({});
    } else {
      if (type === "fromDate" || type === "toDate") {
        setDates((prevDates) => ({
          ...prevDates,
          [type]: value,
        }));
      } else {
        setFilterData((prevFilters) => ({
          ...prevFilters,
          [type]: value,
        }));
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.get("/userActivity/list", {
        params: {
          ...filterData,
          fromDate: dates.fromDate,
          toDate: dates.toDate,
        },
      });
      setRowData(response.data.data || []);
      setFilterSelected(true);
    } catch (error) {
      console.error(error);
      toast.error("No activities found for the given filters");
    }
  };

  const columns = [
    {
      field: "fullname",
      headerName: "User",
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) => params.value || "Unknown user",
    },
    {
      field: "activity",
      headerName: "Activity",
      flex: 0.6,
      minWidth: 120,
    },
    {
      field: "noOfLikes",
      headerName: "Likes",
      flex: 0.4,
      minWidth: 80,
    },
    {
      field: "remarks",
      headerName: "Caption / Remarks",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "createdAt",
      headerName: "Posted",
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "yyyy-MM-dd");
      },
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <ToastContainer />
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Header title="USER ACTIVITY" />
      </Box>
      <FormControl variant="standard" sx={{ m: 2, minWidth: 200 }}>
        <InputLabel id="select-standard-label">user</InputLabel>
        <Select
          id="select-standard"
          onChange={(e) => handleChange(e, "userId")}
          onOpen={handleOpen}
          label="user"
        >
          <MenuItem value="all">All</MenuItem>
          {isDataFetched &&
            users.map((user) => (
              <MenuItem key={user.fullname} value={user._id}>
                {user.fullname}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <FormControl variant="standard" sx={{ m: 2, minWidth: 200 }}>
        <div>
          <TextField
            id="from-date"
            label="From"
            type="date"
            value={dates.fromDate}
            onChange={(e) => handleChange(e, "fromDate")}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            id="to-date"
            label="To"
            type="date"
            value={dates.toDate}
            onChange={(e) => handleChange(e, "toDate")}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ marginLeft: 2 }}
          />
        </div>
      </FormControl>

      <FormControl variant="standard" sx={{ m: 2, minWidth: 100 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
        >
          Filter
        </Button>
      </FormControl>

      <Box
        height="80vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.success,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          getRowId={(row) => row.serialNumber}
          rows={rowData}
          columns={columns}
          autoHeight
          disableColumnMenu
          disableColumnSelector
          hideFooter
          rowCount={(rowData && rowData.total) || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{
            Toolbar: DataGridCustomToolbar,
          }}
          componentsProps={{
            toolbar: {
              searchInput,
              setSearchInput,
              setSearch,
              columns,
              rowData,
            },
          }}
        />
      </Box>
    </Box>
  );
};
export default UserActivity;
